import { Box, Typography } from '@mui/material';
import { DateFormats } from '@uplift/constants';
import { CalendarDateService, DateObject } from '@uplift/services';
import { useCallback, useMemo, useState } from 'react';
import { FixedDatePicker } from '../FixedDatePicker/fixed-datepicker.component';
import { getSlotHash, getTimeSlotsForDateFromHash, getTriadTimeSlotsForDateFromHash } from '../helpers';
import { SchedulerProps } from '../scheduler.component';
import { SelectDate } from '../SelectDate/select-date.component';
import { Timeslots } from '../Timeslots/timeslots.main';
import { TriadTimeslots } from '../Timeslots/triad-timeslots.main';
import { StandardTimezoneSelect } from '../../UserTimezoneSelect';

export type UseSchedulerProps = SchedulerProps;

export const useScheduler = ({
  dayParting = false,
  daysAheadLimit: daysAheadLimitProp = 14,
  ...props
}: UseSchedulerProps) => {
  const today = useMemo(() => CalendarDateService.date().startOf('day'), []);

  const slotHash = useMemo(() => getSlotHash(props.timeslots), [ props.timeslots ]);

  const daysAheadLimitDate = useMemo(() => {
    let daysAhead = daysAheadLimitProp;

    const slotHashDates = Object.keys(slotHash);
    const finalDate = slotHashDates[slotHashDates.length - 1];

    if (finalDate) {
      const finalDateObject = CalendarDateService.date(finalDate).startOf('day');
      const daysFromTodayToFinalDate = finalDateObject.diff(today, 'days');
      const daysToRemove = daysFromTodayToFinalDate < daysAhead
        ? daysAhead - daysFromTodayToFinalDate
        : 0;

      if (daysToRemove) {
        daysAhead -= daysToRemove;
      }
    } else {
      daysAhead = 0;
    }

    const offset = props.isTriad ? 2 : 0;
    const daysAheadLimit = daysAhead - offset;

    return today.add(daysAheadLimit, 'day').startOf('day');
  }, [ daysAheadLimitProp, props.isTriad, slotHash, today ]);

  const [ selectedSlot, setSelectedSlot ] = useState<DateObject | null>(props.initialSelectedSlot ?? props.timeslots[0] ?? null);
  const [ currentDate, setCurrentDate ] = useState<DateObject>(props?.timeslots[0]?.startOf('day') ?? today);

  const onGoToNextAvailabilityHandler = useCallback((nextDate: DateObject) => {
    setCurrentDate(nextDate);
  }, []);

  const getNextAvailabilityHandler = useCallback(() => {
    const nextDate = props.timeslots.find(timeslot => timeslot.isAfter(currentDate));

    if (!nextDate) return null;
    return nextDate.startOf('day');
  }, [ currentDate, props.timeslots ]);

  const onSetSelectedSlotHandler = useCallback((dateObject: DateObject) => {
    if (props.isTriad) {
      setSelectedSlot(dateObject);
    } else {
      props.onScheduleSession?.(dateObject);
    }
  }, [ props ]);

  const selectDate = useMemo(() => {
    if (props.dateDisplayOnly) {
      return (
        <Box pl={1}>
          <Typography>{currentDate.format(DateFormats.fullWeekdayFullMonthDate)}</Typography>
        </Box>
      );
    } else {
      return (
        <SelectDate
          currentDate={currentDate}
          onSetCurrentDate={setCurrentDate}
          backDisabled={currentDate.isBefore(today) || currentDate.isSame(today)}
          forwardDisabled={currentDate.isAfter(daysAheadLimitDate) || currentDate.isSame(daysAheadLimitDate)}
          isTriad={props.isTriad}
          onDisabledForwardClick={props.onDisabledForwardClick}
        />
      );
    }
  }, [ props.dateDisplayOnly, props.isTriad, props.onDisabledForwardClick, currentDate, today, daysAheadLimitDate ]);

  const timeslots = useMemo(() => {
    if (props.isTriad) {
      const triadDateTimeSlots = getTriadTimeSlotsForDateFromHash(slotHash, currentDate);

      return (
        <TriadTimeslots
          slotsColumns={triadDateTimeSlots}
          selectedSlot={selectedSlot}
          onSetSelectedSlot={onSetSelectedSlotHandler}
          getNextAvailabilityDate={getNextAvailabilityHandler}
          onGoToNextAvailability={onGoToNextAvailabilityHandler}
        />
      );
    } else {
      const currentDateTimeSlots = getTimeSlotsForDateFromHash(slotHash, currentDate);

      return (
        <Timeslots
          slots={currentDateTimeSlots}
          dayParting={dayParting}
          getNextAvailabilityDate={getNextAvailabilityHandler}
          onGoToNextAvailability={onGoToNextAvailabilityHandler}
          onSetSelectedSlot={onSetSelectedSlotHandler}
        />
      );
    }

  }, [ currentDate, dayParting, getNextAvailabilityHandler, onGoToNextAvailabilityHandler, onSetSelectedSlotHandler, props.isTriad, selectedSlot, slotHash ]);

  const timezoneSelect = useMemo(() => {
    if (props.hideTimezoneSelect) {
      return null;
    } else {
      return <StandardTimezoneSelect disabled={props.disableTimezoneSelect} />;
    }
  }, [ props.disableTimezoneSelect, props.hideTimezoneSelect ]);

  const datePicker = useMemo(() => {
    if (props.withDatePicker) {
      return (
        <FixedDatePicker
          timeslots={props.timeslots}
          currentDate={currentDate}
          setCurrentDate={setCurrentDate}
        />
      );
    } else {
      return null;
    }
  }, [ currentDate, props.timeslots, props.withDatePicker ]);

  const onScheduleSessionClickHandler = () => {
    if (!selectedSlot) return;

    props.onScheduleSession?.(selectedSlot);
  };

  return {
    timeslots,
    selectDate,
    timezoneSelect,
    datePicker,
    onScheduleSessionClick: onScheduleSessionClickHandler,
    currentDate,
    selectedSlot
  };
};
